import React, { Component } from "react";
import { subMilliseconds, subMonths } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";
import styled from "styled-components";

import { defined } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { duration } from "@formant/core/model/duration";
import { IAnalyticsLayout } from "@formant/core/model/IAnalyticsLayout";
import { IAnalyticsModuleParameters } from "@formant/core/model/IAnalyticsModuleParameters";
import { IDevice } from "@formant/core/model/IDevice";
import { IFilter } from "@formant/core/model/IFilter";
import { IPartialViewConfiguration } from "@formant/core/model/IPartialViewConfiguration";
import { IShare } from "@formant/core/model/IShare";
import { IViewConfiguration } from "@formant/core/model/IViewConfiguration";
import { ModuleMode } from "@formant/core/model/ModuleMode";
import { ModuleType } from "@formant/core/model/ModuleType";
import { primary } from "@formant/ui/component/common/Colors";
import { Page } from "@formant/ui/component/common/Page";
import { Typography } from "@formant/ui/component/common/Typography";

import { track } from "../../analytics";
import {
    analyticsModuleStore,
    analyticsStore,
    appStore,
    observeStore,
} from "../../stores";
import { formatDate } from "../../utils/formatDate";
import { Button } from "../common/Button";
import { RestrictedComponent } from "../common/RestrictedComponent";
import { Scrollable } from "../common/Scrollable";
import { Select } from "../common/Select";
import { ExportTelemetryPopupAnalytics } from "./ExportTelemetryPopupAnalytics";
import { Modules } from "./Modules";
import * as styles from "./ObserveLayout.scss";
import { DateEdit } from "./Timeline/DateEdit";
import { ValueTooltip } from "./ValueTooltip";

export interface IAnalyticsLayoutProps {
    context: IFilter;
    device?: IDevice;
    layout: IAnalyticsLayout;
    filter?: IFilter;
    configuration: IViewConfiguration[];
    noGrid?: boolean;
    share?: IShare;
    onSaveLayout?: (layout: IAnalyticsLayout) => void;
    onChangeLayout?: (layout: IAnalyticsLayout) => Promise<void>;
    onRemoveModule?: (streamName: string) => void;
    onChangeFilter?: (filter: IFilter) => void;
    onChangeConfiguration?: (value: IPartialViewConfiguration) => void;
}

@observer
export class AnalyticsLayout extends Component<IAnalyticsLayoutProps> {
    @observable
    private interval: number = getInterval(this.props.layout);

    @observable
    private startDate: Date | undefined = getStartDate(this.props.layout);

    @observable
    private endDate: Date | undefined = getEndDate(this.props.layout);

    @observable
    private editingStartDate: boolean = false;

    @observable
    private editingEndDate: boolean = false;

    @observable
    private datePickerEnabled: boolean = true;

    componentDidMount(): void {
        track("Analytics page viewed");
        // HANDLE REQUEST FROM A CUSTOM MODULE
        window.addEventListener("message", this.handleDisableDatePicker);
    }

    componentWillUnmount(): void {
        window.removeEventListener("message", this.handleDisableDatePicker);
    }

    handleDisableDatePicker = (e: MessageEvent<any>) => {
        const msg = e.data;
        if (msg.type === "hide_analytics_date_picker") {
            this.datePickerEnabled = false;
        }
    };

    timescaleOptions = [
        { label: "1 day", value: duration.day },
        { label: "1 week", value: duration.week },
        { label: "1 month", value: duration.month },
        { label: "3 months", value: 3 * duration.month },
        { label: "6 months", value: 6 * duration.month },
        { label: "1 year", value: 12 * duration.month },
        { label: "Custom Time Period", value: 0 },
    ];

    timescaleLabel = (_: number) =>
        this.timescaleOptions.find(option => option.value === _)?.label ||
        this.timescaleOptions.find(option => option.value === duration.month)
            ?.label ||
        this.timescaleOptions[0].label;
    render() {
        const { focusedStream, fullscreen } = observeStore;
        const { timescaleLabel, timescaleOptions, datePickerEnabled } = this;

        const {
            context,
            device,
            filter,
            layout,
            onChangeLayout,
            onChangeFilter,
            configuration,
            onChangeConfiguration,
            noGrid,
            share,
        } = this.props;

        // we need to make sure
        // these queries are ran before
        // all of the modules analytics queries
        // go off. (Prevents of a wall of sql from blocking).

        analyticsStore.listFilteredStreams({
            deviceIds: filter?.deviceIds || context?.deviceIds || [],
        });
        analyticsStore.listTaskReports({
            deviceIds: filter?.deviceIds || context?.deviceIds || [],
        });

        return (
            <Page className={styles.observe}>
                <PageContainer>
                    <ScrollContent
                        className={styles.content}
                        dark={true}
                        preventScroll={false}
                    >
                        <Modules
                            context={context}
                            filter={filter}
                            layout={layout}
                            editingLayout={true}
                            configuration={configuration}
                            device={device}
                            activeEvent={undefined}
                            focusedModule={focusedStream}
                            fullscreen={fullscreen}
                            onSelectTime={() => {}}
                            onSeek={() => {}}
                            onChangeFilter={onChangeFilter}
                            onChangeConfiguration={onChangeConfiguration}
                            onChangeLayout={onChangeLayout}
                            onFocusModule={this.onFocusModule}
                            onToggleFullscreen={() =>
                                observeStore.toggleFullscreen(true)
                            }
                            noGrid={noGrid}
                            onComment={undefined}
                            onAnnotate={undefined}
                            onDemandData={undefined}
                            onRemoveModule={this.handleRemoveModule}
                            hideDevice={false}
                            onConfigureModule={this.onConfigureModule}
                            moduleMode={ModuleMode.Observe}
                            analyticsOnly={true}
                            share={share}
                        />
                        <ValueTooltip />
                    </ScrollContent>
                </PageContainer>
                <Bar>
                    <LeftBar>
                        {datePickerEnabled && (
                            <Select
                                value={this.interval}
                                options={timescaleOptions.map(_ => _.value)}
                                bottom={true}
                                label={timescaleLabel}
                                icon="duration"
                                title="Timeline interval"
                                responsive={true}
                                onChange={this.handleChangeInterval}
                            />
                        )}

                        <>
                            {this.editingStartDate && (
                                <DateEdit
                                    time={this.startDate || new Date()}
                                    maxTime={this.endDate || false}
                                    onDateChanged={this.handleChangeStartDate}
                                    onClose={this.onStopEditingDate}
                                />
                            )}

                            <TimeButton onClicked={this.onClickEditStartDate}>
                                <Typography type="minimum" display="block">
                                    Start Date
                                </Typography>
                                <Typography
                                    type="label"
                                    display="block"
                                    color="silver"
                                >
                                    {formatDate(this.startDate || new Date())}
                                </Typography>
                            </TimeButton>

                            {this.editingEndDate && (
                                <DateEdit
                                    time={this.endDate || new Date()}
                                    minTime={this.startDate || false}
                                    onDateChanged={this.handleChangeEndDate}
                                    onClose={this.onStopEditingDate}
                                />
                            )}

                            <TimeButton onClicked={this.onClickEditEndDate}>
                                <Typography type="minimum" display="block">
                                    End Date
                                </Typography>
                                <Typography
                                    type="label"
                                    display="block"
                                    color="silver"
                                >
                                    {formatDate(this.endDate || new Date())}
                                </Typography>
                            </TimeButton>
                        </>
                    </LeftBar>
                </Bar>
                <RestrictedComponent
                    resource="integrations"
                    requiredAccess="operator"
                >
                    {observeStore.exportAnalyticsPopupQuery && (
                        <ExportTelemetryPopupAnalytics
                            query={observeStore.exportAnalyticsPopupQuery}
                        />
                    )}
                </RestrictedComponent>
            </Page>
        );
    }

    private handleRemoveModule = (streamName: string) => {
        const { layout, onSaveLayout } = this.props;

        appStore.setConfirm({
            title: "Delete module",
            okCopy: "Delete module",
            message: <>Are you sure you want to delete this module?</>,
            critical: true,
            onOk: () => {
                const id = streamName.replace("analytics-module:", "");
                if (id === observeStore.configuringAnalyticsId) {
                    observeStore.configuringAnalyticsId = undefined;
                }

                delete layout[streamName];

                const moduleId = (
                    analyticsModuleStore.get(
                        streamName.replace("analytics-module:", ""),
                    ) || undefined
                )?.id;

                if (moduleId) {
                    fork(analyticsModuleStore.delete(moduleId));
                }
                onSaveLayout?.(layout);
                observeStore.fullscreen = false;
            },
        });
    };

    private onClickEditStartDate = () => {
        this.editingStartDate = true;
    };

    private onClickEditEndDate = () => {
        this.editingEndDate = true;
    };

    private onStopEditingDate = () => {
        this.editingEndDate = false;
        this.editingStartDate = false;
    };

    private onConfigureModule = async (moduleName: string) => {
        observeStore.temporarilyConfiguringModule = [moduleName];
        observeStore.isCustomizing = true;
        observeStore.configuringAnalyticsId = moduleName.replace(
            "analytics-module:",
            "",
        );
    };

    private onFocusModule = (stream?: [string, ModuleType?]) => {
        if (
            !!stream &&
            stream.length > 0 &&
            (observeStore.isCustomizing || observeStore.configuringAnalyticsId)
        ) {
            fork(this.onConfigureModule(stream[0]));
        }
        observeStore.focusedStream = stream
            ? [stream[0], stream[1]]
            : undefined;
    };

    private handleChangeDateRange = (
        interval: number | undefined,
        startTime: Date | undefined,
        endTime: Date | undefined,
    ) => {
        this.onStopEditingDate();

        const { layout, onChangeLayout } = this.props;
        if (interval !== undefined) {
            this.interval = interval;
        } else if (startTime !== undefined || endTime !== undefined) {
            // switch interval to custom
            this.interval = 0;
        }

        if (startTime) {
            this.startDate = startTime;
            observeStore.analyticsTempStartDate = startTime.toISOString();
        }

        if (endTime) {
            this.endDate = endTime;
            observeStore.analyticsTempEndDate = endTime.toISOString();
        }

        for (const moduleId of Object.keys(layout)) {
            if (!layout?.[moduleId]?.configuration?.parameters) {
                return;
            }
            const params = layout[moduleId]?.configuration?.parameters as
                | IAnalyticsModuleParameters
                | undefined;

            if (params && params?.query) {
                if (startTime) {
                    params.query.start = startTime.toISOString();
                }

                if (endTime) {
                    params.query.end = endTime.toISOString();
                }

                if (interval === -1) {
                    params.query.start = undefined;
                    params.query.end = undefined;
                    this.startDate = undefined;
                    this.endDate = undefined;
                }
            }
        }
        if (onChangeLayout) {
            fork(onChangeLayout(layout));
        }
        this.onStopEditingDate();
    };

    private handleChangeInterval = (value: number) => {
        this.interval = value;
        const timeLabel = defined(
            this.timescaleOptions.find(_ => _.value === value),
        );
        track("Date range option selected ", { value: timeLabel.label });

        if (value === 12 * duration.month) {
            const end = new Date();
            const start = subMonths(end, 12);
            this.handleChangeDateRange(value, start, end);
            return;
        }

        if (value !== 0) {
            const start = subMilliseconds(new Date(), value);
            const end = new Date();
            this.handleChangeDateRange(value, start, end);
        }

        if (value === -1) {
            this.handleChangeDateRange(-1, undefined, undefined);
        }

        if (value === 0) {
            const end = new Date();
            const start = subMonths(end, 3);
            this.handleChangeDateRange(0, start, end);
        }
    };

    private handleChangeStartDate = (date: Date): void => {
        this.handleChangeDateRange(undefined, date, undefined);
    };

    private handleChangeEndDate = (date: Date): void => {
        this.handleChangeDateRange(undefined, undefined, date);
    };
}

const getInterval = (layout: IAnalyticsLayout): number => {
    const defaultStart = observeStore.analyticsTempStartDate;
    const defaultEnd = observeStore.analyticsTempEndDate;

    if (defaultStart && defaultEnd) {
        return (
            new Date(defaultEnd).getTime() - new Date(defaultStart).getTime()
        );
    }

    const query = (
        Object.values(layout)?.[0]?.configuration
            ?.parameters as IAnalyticsModuleParameters
    )?.query;

    if (!query?.start && !query?.end) {
        return -1;
    }

    if (query?.start && query.end) {
        return new Date(query.end).getTime() - new Date(query?.start).getTime();
    }

    return 1 * duration.month;
};

const getStartDate = (layout: IAnalyticsLayout): Date | undefined => {
    const defaultStart = observeStore.analyticsTempStartDate;
    if (defaultStart) {
        return new Date(defaultStart);
    }

    const date = (
        Object.values(layout)?.[0]?.configuration
            ?.parameters as IAnalyticsModuleParameters
    )?.query?.start;

    if (date) {
        observeStore.analyticsTempStartDate = date;
        return new Date(date);
    }

    return undefined;
};

const getEndDate = (layout: IAnalyticsLayout): Date | undefined => {
    const defaultEnd = observeStore.analyticsTempEndDate;

    if (defaultEnd) {
        return new Date(defaultEnd);
    }
    const date = (
        Object.values(layout)?.[0]?.configuration
            ?.parameters as IAnalyticsModuleParameters
    )?.query?.end;

    if (date) {
        observeStore.analyticsTempEndDate = date;
        return new Date(date);
    }

    return undefined;
};

const PageContainer = styled.div`
    flex: 1;
    display: flex;
    overflow: hidden;
    background: var(--formant-color-background);
    position: relative;
`;

const ScrollContent = styled(Scrollable)`
    flex: 1;
    padding-right: 4px;
    padding-top: 1px;
`;

const TimeButton = styled(Button)`
    margin-left: 2px;
    text-align: center;
    display: flex;
    flex-direction: column;
    margin: 2rem;
`;

const Bar = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 5rem;
    overflow: unset;
    background-color: ${primary.black};
    padding-left: 2rem;

    button[class~="react-calendar__navigation__prev2-button"] {
        display: none;
    }

    button[class~="react-calendar__navigation__next2-button"] {
        display: none;
    }

    button[disabled][class~="react-calendar__navigation__label"] {
        text-transform: uppercase;
        color: white;
        font-size: 0.6875rem;
        flex-grow: 0 !important;
        width: 5rem;
    }
`;

const LeftBar = styled.div`
    display: flex;
    flex-direction: row;
`;
